import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styles from './cdn-image.module.scss.js';
import Video from './components/Video.js';
import Image from './components/Image.js';
import useIntersection from '../../hooks/useIntersection/index.js';
import { isGifUrl, prefersReducedMotion, supportsImageLazyLoad } from '../../utils/misc-utils.js';

// Using a blank image will prevent broken image icon while image loads
const blankSquareImage = 'data:image/gif;base64,R0lGODlhAQABAIAAAP///////yH5BAEAAAEALAAAAAABAAEAAAICTAEAOw==';

/**
 Displays an image from the BuzzFeed image CDN (img.buzzfeed.com)
 with the option to play/pause GIF if image is a GIF
 Expects props src {string}, alt {string}, className {string}, id {string}
 */

const CdnImage = _ref => {
  let {
    alt = '',
    className = '',
    height = '',
    id = '',
    lazy = false,
    // Setting default value to "eager" as this is the default behavior (no native lazy loading)
    // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img#attr-loading
    loading = 'eager',
    fetchpriority = 'auto',
    sizes = '',
    src = '',
    srcSet = '',
    width = ''
  } = _ref;
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const isGif = isGifUrl(src);
  const isMotionAllowed = isGif && !prefersReducedMotion();

  /**
   * This will return empty until we have something to observe.
   * Lazy loading strategy will use IntersectionObserver only when one of the following criteria are met
   * (`loading` attribute takes precedence over the `lazy` attribute):
   *
   *   1) 'loading' attribute value is "lazy" AND
   *   1a) client does not support native lazy loading OR
   *   1b) is a video (video elements don't support `loading` attribute)
   *
   *   2) 'loading' attribute value is NOT "lazy" AND
   *   2a) the `lazy` prop value is `true` AND
   *   2b) We have a static image or video
   */
  const {
    isIntersecting,
    setObservable
  } = useIntersection({
    rootMargin: '300px',
    once: true
  });
  const useLazyObserver = loading === 'lazy' && (isMotionAllowed || !supportsImageLazyLoad()) || lazy && (isMotionAllowed || loading !== 'lazy');
  if (!src) {
    return null;
  }
  const classNames = className ? className.split(' ') : [];
  if (isGif) {
    classNames.push('js-gif-container', styles.cdnImage);
    if (isPlaying && isMotionAllowed) {
      classNames.push(isLoading ? styles.gif__loading : styles.gif__playing);
    }
  }

  // If we use lazy observer, we only set the image source when we come into view for the first time
  const imgSrc = useLazyObserver ? isIntersecting ? src : blankSquareImage : src;
  const isImgSrcBlank = imgSrc === blankSquareImage;
  return /*#__PURE__*/React.createElement("div", {
    className: classNames.join(' '),
    ref: useLazyObserver ? setObservable : null
  }, isMotionAllowed && /*#__PURE__*/React.createElement(Video, {
    alt: alt,
    height: `${height}`,
    id: id,
    isPlaying: isPlaying,
    setIsLoading: setIsLoading,
    setIsPlaying: setIsPlaying,
    src: !isImgSrcBlank ? imgSrc : '' /* only set video src if imgSrc is set (re: lazyloading) */,
    width: `${width}`
  }), !isMotionAllowed && /*#__PURE__*/React.createElement(Image, {
    alt: alt,
    height: `${height}`,
    loading: !useLazyObserver ? loading : null,
    fetchpriority: fetchpriority,
    sizes: !isImgSrcBlank ? sizes : null /* only set sizes if imgSrc is set (re: lazyloading) */,
    src: imgSrc,
    srcSet: !isImgSrcBlank ? srcSet : null /* only set srcSet if imgSrc is set (re: lazyloading) */,
    width: `${width}`
  }));
};
CdnImage.propTypes = {
  alt: PropTypes.string,
  className: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  lazy: PropTypes.bool,
  loading: PropTypes.oneOf(['eager', 'lazy']),
  src: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export { CdnImage, blankSquareImage, CdnImage as default };
